import React, { useState, useEffect, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import axios from 'axios';
import html2canvas from 'html2canvas';

function Messages({ user }) {
  // ========== STATE ==========
  const [messages, setMessages] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [showQRLink, setShowQRLink] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadMode, setUploadMode] = useState(false);
  const [ocrProgress, setOcrProgress] = useState(0);
  const [errors, setErrors] = useState({});
  const [currentQRId, setCurrentQRId] = useState(null);
  
  const [formData, setFormData] = useState({
    recipient_name: '',
    message_text: '',
    message_image: null,
    release_date: new Date(new Date().getTime() + 7 * 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 16),
  });
  
  const [editFormData, setEditFormData] = useState({
    recipient_name: '',
    message_text: '',
    release_date: '',
  });
  
  const [qrData, setQrData] = useState(null);

  // ========== LIFECYCLE ==========
  useEffect(() => {
    const loadMessages = async () => {
      try {
        const res = await axios.get(
          `http://localhost:4500/api/messages/list?email=${user.email}`
        );
        if (res.data.success) setMessages(res.data.messages);
      } catch (err) {
        console.error('Error:', err);
      }
    };
    loadMessages();
  }, [user.email]);

  const refreshMessages = useCallback(async () => {
    try {
      const res = await axios.get(
        `http://localhost:4500/api/messages/list?email=${user.email}`
      );
      if (res.data.success) setMessages(res.data.messages);
    } catch (err) {
      console.error('Error:', err);
    }
  }, [user.email]);

  // ========== OCR IMAGE UPLOAD ==========
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!formData.recipient_name.trim()) {
      alert('Empfänger Name erforderlich');
      return;
    }
    setLoading(true);
    setOcrProgress(10);
    const start = Date.now();
    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const imageData = event.target.result;
          setOcrProgress(30);
          const res = await axios.post('http://localhost:4500/api/ocr', {
            image: imageData,
          });
          if (res.data.success && res.data.text) {
            const text = res.data.text;
            setOcrProgress(90);
            const img = document.createElement('img');
            img.onload = () => {
              const canvas = document.createElement('canvas');
              canvas.width = img.width;
              canvas.height = img.height;
              const ctx = canvas.getContext('2d');
              ctx.drawImage(img, 0, 0);
              const compressedImage = canvas.toDataURL('image/png');
              setFormData((prev) => ({
                ...prev,
                message_image: compressedImage,
                message_text: text,
              }));
              setOcrProgress(100);
              setTimeout(() => {
                setLoading(false);
                setOcrProgress(0);
              }, 500);
            };
            img.src = imageData;
          }
        } catch (err) {
          alert('OCR error: ' + err.message);
          setLoading(false);
          setOcrProgress(0);
        }
      };
      reader.onerror = () => {
        alert('Read error');
        setLoading(false);
        setOcrProgress(0);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      alert('Upload error: ' + err.message);
      setLoading(false);
      setOcrProgress(0);
    }
  };

  // ========== CREATE MESSAGE ==========
  const handleCreateMessage = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.recipient_name.trim()) newErrors.recipient_name = true;
    if (!formData.message_text.trim()) newErrors.message_text = true;
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    setLoading(true);
    try {
      const res = await axios.post(
        'http://localhost:4500/api/messages/create',
        {
          email: user.email,
          recipient_name: formData.recipient_name,
          message_text: formData.message_text,
          message_image: formData.message_image,
          release_date: formData.release_date,
        }
      );
      if (res.data.success) {
        const qrUrl = `${window.location.origin}/view/${res.data.token}`;
        setQrData({
          token: res.data.token,
          url: qrUrl,
          recipient_name: formData.recipient_name,
        });
        setFormData({
          recipient_name: '',
          message_text: '',
          message_image: null,
          release_date: new Date(new Date().getTime() + 7 * 24 * 60 * 60 * 1000)
            .toISOString()
            .slice(0, 16),
        });
        setUploadMode(false);
        setShowForm(false);
        refreshMessages();
      }
    } catch (err) {
      alert('Error: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  // ========== EDIT MESSAGE ==========
  const startEdit = (msg) => {
    setEditingMessage(msg.id);
    setEditFormData({
      recipient_name: msg.recipient_name,
      message_text: msg.message_text,
      release_date: msg.release_date || '',
    });
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editFormData.recipient_name.trim() || !editFormData.message_text.trim()) {
      alert('Alle Felder erforderlich');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.put(
        `http://localhost:4500/api/messages/update/${editingMessage}`,
        {
          email: user.email,
          recipient_name: editFormData.recipient_name,
          message_text: editFormData.message_text,
          release_date: editFormData.release_date,
        }
      );
      if (res.data.success) {
        refreshMessages();
        setEditingMessage(null);
        setSelectedMessage(null);
      }
    } catch (err) {
      alert('Error: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  // ========== DELETE MESSAGE ==========
  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Nachricht wirklich löschen?')) return;
    try {
      const res = await axios.post(
        'http://localhost:4500/api/messages/delete',
        { email: user.email, messageId }
      );
      if (res.data.success) {
        refreshMessages();
        setSelectedMessage(null);
      }
    } catch (err) {
      alert('Error: ' + (err.response?.data?.error || err.message));
    }
  };

  // ========== TOGGLE MESSAGE SELECTION ==========
  const toggleMessageSelection = (msg) => {
    if (selectedMessage?.id === msg.id) {
      setSelectedMessage(null);
      setEditingMessage(null);
      setShowQRLink(null);
    } else {
      setSelectedMessage(msg);
      setEditingMessage(null);
      setShowQRLink(null);
    }
  };

  // ========== DOWNLOAD COMPLETE POSTCARD ==========
  const downloadQR = async () => {
    try {
      const element = document.querySelector(`#postcard-card-${currentQRId || 'main'}`);
      
      if (!element) {
        console.error('Postcard element not found');
        alert('Postkarte nicht gefunden - bitte neu laden');
        return;
      }

      // Capture the postcard as image using html2canvas
      const canvas = await html2canvas(element, {
        scale: 2,
        backgroundColor: null,
        logging: true,
        useCORS: true,
      });

      // Convert canvas to blob and download
      canvas.toBlob((blob) => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `timelock_postkarte_${Date.now()}.png`;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
        console.log('Postcard downloaded successfully');
      }, 'image/png', 1.0);
    } catch (err) {
      console.error('Download error:', err);
      alert('Download fehlgeschlagen: ' + err.message);
    }
  };

  // ========== COMPONENT: LETTER PREVIEW ==========
  const LetterPreview = ({ name, text, image = null, editable = false, onEditChange = null }) => (
    <div
      style={{
        background: 'linear-gradient(135deg, #f5f1e8 0%, #fffbf5 100%)',
        border: '1px solid rgba(200, 180, 150, 0.3)',
        borderRadius: '4px',
        padding: '40px 50px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.6)',
        color: '#2d2d2d',
        fontFamily: 'Georgia, serif',
      }}
    >
      <h2 style={{
        fontFamily: "'Great Vibes', cursive",
        fontSize: '32px',
        fontStyle: 'italic',
        marginBottom: '20px',
        marginTop: 0,
      }}>
        Lieber {name},
      </h2>
      
      {editable && onEditChange ? (
        <textarea
          value={text}
          onChange={(e) => onEditChange(e.target.value)}
          style={{
            width: '100%',
            padding: '10px',
            background: '#fffbf5',
            border: '1px solid rgba(200, 180, 150, 0.5)',
            borderRadius: '4px',
            color: '#2d2d2d',
            minHeight: '120px',
            boxSizing: 'border-box',
            fontFamily: 'Georgia, serif',
            fontSize: '14px',
            lineHeight: '1.6',
          }}
        />
      ) : (
        <p style={{
          fontSize: '15px',
          lineHeight: '1.8',
          whiteSpace: 'pre-wrap',
          margin: '0 0 15px 0',
        }}>
          {text}
        </p>
      )}

      {image && (
        <img
          src={image}
          alt="Hochgeladenes Bild"
          style={{
            maxWidth: '100%',
            maxHeight: '300px',
            borderRadius: '4px',
            marginTop: '15px',
            marginBottom: '15px',
          }}
        />
      )}
    </div>
  );

  // ========== COMPONENT: POSTCARD LAYOUT WITH MESSAGE MODE ==========
  const PostcardLayoutWithMessage = ({ name, text, senderName = 'admin', qrUrl, qrId = 'main' }) => (
    <div
      id={`postcard-card-${qrId}`}
      style={{
        background: 'linear-gradient(135deg, #f5f1e8 0%, #fffbf5 100%)',
        border: '3px solid #8b7355',
        borderRadius: '0px',
        padding: '40px 45px',
        boxShadow: '0 15px 40px rgba(0, 0, 0, 0.4)',
        color: '#2d2d2d',
        fontFamily: 'Georgia, serif',
        position: 'relative',
        minHeight: '560px',
        width: '100%',
        maxWidth: '980px',
        margin: '0 auto',
        boxSizing: 'border-box',
        aspectRatio: '16 / 10',
      }}
    >
      {/* Horizontal Divider - Mitte */}
      <div
        style={{
          position: 'absolute',
          left: '45px',
          right: '45px',
          top: '50%',
          height: '1px',
          background: 'rgba(200, 180, 150, 0.5)',
          pointerEvents: 'none',
        }}
      />

      {/* Vertical Divider - Mitte (VISIBLE) */}
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: '40px',
          bottom: '40px',
          width: '2px',
          background: '#8b7355',
          pointerEvents: 'none',
        }}
      />

      {/* ===== LEFT SIDE: QR + HINWEIS (BIGGER) ===== */}
      <div
        style={{
          position: 'absolute',
          left: '45px',
          top: '40px',
          bottom: '40px',
          width: '270px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          paddingRight: '30px',
        }}
      >
        {/* QR Code - BIGGER & CENTERED */}
        <div
          style={{
            background: 'white',
            padding: '12px',
            border: '2px solid #999',
            display: 'flex',
            justifyContent: 'center',
            boxShadow: '0 3px 8px rgba(0,0,0,0.2)',
            margin: '0 auto',
          }}
          id={`qrcode_${qrId}`}
        >
          <QRCodeSVG value={qrUrl} size={190} level="H" includeMargin={false} />
        </div>

        {/* HINWEIS Text - Bottom Left */}
        <div
          style={{
            fontSize: '13px',
            color: '#555',
            fontStyle: 'italic',
            lineHeight: '1.6',
            textAlign: 'left',
            fontWeight: '500',
          }}
        >
          <strong style={{ color: '#8b5a3c', fontSize: '13px', fontWeight: '700' }}>Hinweis:</strong> Zum Öffnen der Website einfach den QR-Code mit der Kamera oder einer Scanner-App erfassen.
        </div>
      </div>

      {/* ===== RIGHT SIDE: MESSAGE (NARROWER) ===== */}
      <div
        style={{
          marginLeft: '330px',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          paddingRight: '15px',
        }}
      >
        {/* Top Right: PostCARD Text + Lock Box */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '25px',
            minHeight: '75px',
          }}
        >
          {/* PostCARD Text - MUCH BIGGER */}
          <div
            style={{
              fontSize: '56px',
              fontWeight: 'bold',
              color: '#8b5a3c',
              letterSpacing: '0px',
              fontStyle: 'italic',
              fontFamily: "'Brush Script MT', 'Great Vibes', cursive",
              lineHeight: '1.0',
              flex: 1,
            }}
          >
            PostCARD
          </div>

          {/* Lock Stamp Box */}
          <div
            style={{
              width: '85px',
              height: '95px',
              border: '2px solid #8b7355',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '52px',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              flexShrink: 0,
              marginLeft: '15px',
            }}
          >
            🔐
          </div>
        </div>

        {/* Von: Sender */}
        <p
          style={{
            fontSize: '16px',
            color: '#8b5a3c',
            fontStyle: 'italic',
            margin: '0 0 28px 0',
            fontWeight: '400',
          }}
        >
          Von: {senderName}
        </p>

        {/* Lieber {name}, - BOLD */}
        <p
          style={{
            fontSize: '18px',
            fontStyle: 'italic',
            color: '#8b5a3c',
            margin: '0 0 12px 0',
            paddingBottom: '6px',
            borderBottom: '2px solid #8b5a3c',
            display: 'inline-block',
            fontWeight: '700',
          }}
        >
          Lieber {name},
        </p>

        {/* Message Text with Writing Lines */}
        <div
          style={{
            flex: 1,
            position: 'relative',
            backgroundImage: 'repeating-linear-gradient(to bottom, transparent 0px, transparent 17px, #e8e0d5 17px, #e8e0d5 21px)',
            backgroundPosition: '0 25px',
            paddingRight: '15px',
            paddingTop: '10px',
            fontSize: '15px',
            lineHeight: '21px',
            whiteSpace: 'pre-wrap',
            wordWrap: 'break-word',
            color: '#2d2d2d',
            overflowY: 'auto',
            maxHeight: '280px',
          }}
        >
          {text}
        </div>
      </div>
    </div>
  );

  const PostcardLayout = ({ name, text, senderName = 'admin', qrUrl, qrId = 'main' }) => (
    <div
      id={`postcard-card-${qrId}`}
      style={{
        background: 'linear-gradient(135deg, #f5f1e8 0%, #fffbf5 100%)',
        border: '3px solid #8b7355',
        borderRadius: '0px',
        padding: '40px 45px',
        boxShadow: '0 15px 40px rgba(0, 0, 0, 0.4)',
        color: '#2d2d2d',
        fontFamily: 'Georgia, serif',
        position: 'relative',
        minHeight: '560px',
        width: '100%',
        maxWidth: '980px',
        margin: '0 auto',
        boxSizing: 'border-box',
        aspectRatio: '16 / 10',
        display: 'flex',
        alignItems: 'stretch',
      }}
    >
      {/* Vertical Divider - Mitte (VISIBLE) */}
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: '40px',
          bottom: '40px',
          width: '2px',
          background: '#8b7355',
          pointerEvents: 'none',
        }}
      />

      {/* ===== LEFT SIDE: QR + HINWEIS ===== */}
      <div
        style={{
          position: 'absolute',
          left: '45px',
          top: '40px',
          bottom: '40px',
          width: 'calc(50% - 47px)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingRight: '30px',
        }}
      >
        {/* QR Code - EXTRA GROSS (352px) */}
        <div
          style={{
            background: 'white',
            padding: '15px',
            border: '3px solid #999',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
            marginTop: '30px',
            marginBottom: '8px',
          }}
          id={`qrcode_${qrId}`}
        >
          <QRCodeSVG value={qrUrl} size={352} level="H" includeMargin={false} />
        </div>

        {/* HINWEIS Text - Bottom Left */}
        <div
          style={{
            fontSize: '12px',
            color: '#666',
            fontStyle: 'italic',
            lineHeight: '1.4',
            textAlign: 'center',
            fontWeight: '500',
            marginBottom: '0px',
          }}
        >
          <strong style={{ color: '#8b5a3c', fontSize: '12px', fontWeight: '700' }}>Hinweis:</strong> QR-Code mit Kamera erfassen
        </div>
      </div>

      {/* ===== RIGHT SIDE: TEXT AT TOP ===== */}
      <div
        style={{
          position: 'absolute',
          left: 'calc(50% + 15px)',
          right: '45px',
          top: '40px',
          display: 'flex',
          flexDirection: 'column',
          paddingLeft: '0px',
        }}
      >
        {/* Top Right: PostCARD Text + Lock Box */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '25px',
            minHeight: '75px',
          }}
        >
          {/* PostCARD Text - AUSEINANDER, NORMAL GEWICHT, GRÖSSER */}
          <div
            style={{
              fontSize: '64px',
              fontWeight: 'normal',
              color: '#8b5a3c',
              letterSpacing: '8px',
              fontStyle: 'italic',
              fontFamily: "'Brush Script MT', 'Great Vibes', cursive",
              lineHeight: '1.0',
              flex: 1,
            }}
          >
            PostCARD
          </div>

          {/* Lock Stamp Box */}
          <div
            style={{
              width: '85px',
              height: '95px',
              border: '2px solid #8b7355',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '52px',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              flexShrink: 0,
              marginLeft: '15px',
            }}
          >
            🔐
          </div>
        </div>

        {/* Von: Sender - NÄHER DARUNTER */}
        <p
          style={{
            fontSize: '16px',
            color: '#8b5a3c',
            fontStyle: 'italic',
            margin: '5px 0 15px 0',
            fontWeight: '400',
          }}
        >
          Von: {senderName}
        </p>

        {/* Lieber {name}, - BOLD - dunkelgrau, ein Absatz höher */}
        <p
          style={{
            fontSize: '18px',
            fontStyle: 'italic',
            color: '#2d2d2d',
            margin: '30px 0 12px 0',
            paddingBottom: '6px',
            borderBottom: '2px solid #2d2d2d',
            display: 'inline-block',
            fontWeight: '700',
          }}
        >
          Lieber {name},
        </p>

        {/* Message - 3 LINES EACH UNDERLINED */}
        <div
          style={{
            marginTop: '30px',
            fontSize: '18px',
            lineHeight: '26px',
            color: '#2d2d2d',
            fontWeight: '700',
            fontStyle: 'italic',
          }}
        >
          {/* Line 1 */}
          <div
            style={{
              paddingBottom: '6px',
              borderBottom: '2px solid #8b5a3c',
              display: 'block',
              marginBottom: '10px',
            }}
          >
            Diese Nachricht wurde speziell für dich
          </div>
          
          {/* Line 2 */}
          <div
            style={{
              paddingBottom: '6px',
              borderBottom: '2px solid #8b5a3c',
              display: 'block',
              marginBottom: '10px',
            }}
          >
            hinterlegt und wartet auf den
          </div>
          
          {/* Line 3 */}
          <div
            style={{
              paddingBottom: '6px',
              borderBottom: '2px solid #8b5a3c',
              display: 'block',
            }}
          >
            richtigen Zeitpunkt...
          </div>
        </div>
      </div>
    </div>
  );

  // ========== RENDER ==========
  return (
    <div className="tab-panel">
      <h2>Nachrichten</h2>
      {!qrData ? (
        <>
          {/* ===== CREATE FORM ===== */}
          <div style={{ marginBottom: '24px' }}>
            {!showForm ? (
              <button
                onClick={() => setShowForm(true)}
                style={{
                  padding: '10px 20px',
                  background: '#a855f7',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                }}
              >
                + Neue Nachricht
              </button>
            ) : (
              <form
                onSubmit={handleCreateMessage}
                style={{
                  background: 'rgba(30, 41, 59, 0.5)',
                  border: '1px solid rgba(168, 85, 247, 0.2)',
                  borderRadius: '10px',
                  padding: '20px',
                }}
              >
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', color: '#94a3b8', marginBottom: '6px', fontSize: '12px' }}>
                    Empfänger Name *
                  </label>
                  <input
                    type="text"
                    placeholder="Name des Empfängers..."
                    value={formData.recipient_name}
                    onChange={(e) => {
                      setFormData({ ...formData, recipient_name: e.target.value });
                      setErrors({ ...errors, recipient_name: false });
                    }}
                    style={{
                      width: '100%',
                      padding: '10px',
                      background: '#0f172a',
                      border: !formData.recipient_name.trim() ? '2px solid #dc2626' : '1px solid rgba(168, 85, 247, 0.3)',
                      borderRadius: '6px',
                      color: '#e2e8f0',
                      boxSizing: 'border-box',
                      backgroundColor: !formData.recipient_name.trim() ? 'rgba(220, 38, 38, 0.1)' : '#0f172a',
                    }}
                  />
                </div>

                <div style={{ marginBottom: '12px', display: 'flex', gap: '8px' }}>
                  <button
                    type="button"
                    onClick={() => setUploadMode(false)}
                    style={{
                      flex: 1,
                      padding: '8px',
                      background: !uploadMode ? 'rgba(168, 85, 247, 0.2)' : 'transparent',
                      border: '1px solid rgba(168, 85, 247, 0.3)',
                      color: '#a855f7',
                      borderRadius: '4px',
                      cursor: 'pointer',
                    }}
                  >
                    ✍️ Brief schreiben
                  </button>
                  <button
                    type="button"
                    onClick={() => setUploadMode(true)}
                    style={{
                      flex: 1,
                      padding: '8px',
                      background: uploadMode ? 'rgba(168, 85, 247, 0.2)' : 'transparent',
                      border: '1px solid rgba(168, 85, 247, 0.3)',
                      color: '#a855f7',
                      borderRadius: '4px',
                      cursor: 'pointer',
                    }}
                  >
                    📸 Handschrift einscannen
                  </button>
                </div>

                {uploadMode ? (
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', color: '#94a3b8', marginBottom: '6px', fontSize: '12px' }}>
                      Bild hochladen (OCR)
                    </label>
                    
                    {/* DRAG-DROP ZONE */}
                    <div
                      style={{
                        border: '2px dashed rgba(168, 85, 247, 0.5)',
                        borderRadius: '8px',
                        padding: '30px 20px',
                        textAlign: 'center',
                        background: formData.recipient_name.trim() ? 'rgba(168, 85, 247, 0.05)' : 'rgba(107, 114, 128, 0.1)',
                        cursor: formData.recipient_name.trim() ? 'pointer' : 'not-allowed',
                        opacity: formData.recipient_name.trim() ? 1 : 0.5,
                        pointerEvents: formData.recipient_name.trim() ? 'auto' : 'none',
                        position: 'relative',
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        if (formData.recipient_name.trim()) {
                          e.currentTarget.style.background = 'rgba(168, 85, 247, 0.15)';
                        }
                      }}
                      onDragLeave={(e) => {
                        e.currentTarget.style.background = formData.recipient_name.trim() ? 'rgba(168, 85, 247, 0.05)' : 'rgba(107, 114, 128, 0.1)';
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        if (formData.recipient_name.trim() && e.dataTransfer.files[0]) {
                          handleImageUpload({ target: { files: e.dataTransfer.files } });
                        }
                      }}
                    >
                      <div style={{ color: '#a855f7', fontSize: '16px', marginBottom: '8px' }}>
                        📤 Klicken Sie hier oder ziehen Sie ein Bild herein
                      </div>
                      <label
                        htmlFor="image-upload"
                        style={{
                          display: 'inline-block',
                          padding: '8px 16px',
                          background: formData.recipient_name.trim() ? '#a855f7' : '#6b7280',
                          color: 'white',
                          borderRadius: '6px',
                          cursor: formData.recipient_name.trim() ? 'pointer' : 'not-allowed',
                          fontWeight: '600',
                          fontSize: '12px',
                          opacity: formData.recipient_name.trim() ? 1 : 0.6,
                        }}
                      >
                        📁 Datei auswählen
                      </label>
                      <input
                        id="image-upload"
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        disabled={!formData.recipient_name.trim() || loading}
                        style={{
                          display: 'none',
                        }}
                      />
                      {!formData.recipient_name.trim() && (
                        <div style={{ color: '#ef4444', fontSize: '11px', marginTop: '8px', fontStyle: 'italic' }}>
                          ⚠️ Bitte geben Sie zuerst einen Namen ein
                        </div>
                      )}
                    </div>
                    
                    {ocrProgress > 0 && ocrProgress < 100 && (
                      <div style={{ marginTop: '8px', color: '#a855f7', textAlign: 'center' }}>
                        ⏳ Erkenne Handschrift...
                      </div>
                    )}

                    {!loading && formData.message_text && (
                      <div style={{ marginTop: '15px' }}>
                        <p style={{ color: '#94a3b8', fontSize: '11px', fontStyle: 'italic', marginBottom: '8px' }}>
                          ℹ️ Text ist editierbar - klick zum Bearbeiten
                        </p>
                        <LetterPreview
                          name={formData.recipient_name || 'Empfänger'}
                          text={formData.message_text}
                          image={formData.message_image}
                          editable={true}
                          onEditChange={(newText) => setFormData({ ...formData, message_text: newText })}
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', color: '#94a3b8', marginBottom: '6px', fontSize: '12px' }}>
                      Nachricht *
                    </label>
                    <textarea
                      value={formData.message_text}
                      onChange={(e) => {
                        setFormData({ ...formData, message_text: e.target.value });
                        setErrors({ ...errors, message_text: false });
                      }}
                      style={{
                        width: '100%',
                        padding: '10px',
                        background: '#0f172a',
                        border: !formData.message_text.trim() ? '2px solid #dc2626' : '1px solid rgba(168, 85, 247, 0.3)',
                        borderRadius: '6px',
                        color: '#e2e8f0',
                        minHeight: '120px',
                        boxSizing: 'border-box',
                        backgroundColor: !formData.message_text.trim() ? 'rgba(220, 38, 38, 0.1)' : '#0f172a',
                      }}
                    />

                    {formData.message_text && (
                      <div style={{ marginTop: '15px' }}>
                        <p style={{ color: '#94a3b8', fontSize: '11px', marginBottom: '8px' }}>
                          👁️ So sieht der Brief aus:
                        </p>
                        <LetterPreview
                          name={formData.recipient_name || 'Empfänger'}
                          text={formData.message_text}
                        />
                      </div>
                    )}
                  </div>
                )}

                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', color: '#94a3b8', marginBottom: '6px', fontSize: '12px' }}>
                    Freigabedatum
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.release_date}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        release_date: e.target.value,
                      })
                    }
                    style={{
                      width: '100%',
                      padding: '10px',
                      background: '#0f172a',
                      border: '1px solid rgba(168, 85, 247, 0.3)',
                      borderRadius: '6px',
                      color: '#e2e8f0',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    type="submit"
                    disabled={loading || !formData.recipient_name.trim() || !formData.message_text.trim()}
                    style={{
                      flex: 1,
                      padding: '10px',
                      background: (loading || !formData.recipient_name.trim() || !formData.message_text.trim()) ? '#6b7280' : '#a855f7',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: (loading || !formData.recipient_name.trim() || !formData.message_text.trim()) ? 'not-allowed' : 'pointer',
                      opacity: (loading || !formData.recipient_name.trim() || !formData.message_text.trim()) ? 0.5 : 1,
                    }}
                  >
                    ✅ Erstellen
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    style={{
                      flex: 1,
                      padding: '10px',
                      background: 'transparent',
                      color: '#a855f7',
                      border: '1px solid rgba(168, 85, 247, 0.5)',
                      borderRadius: '6px',
                      cursor: 'pointer',
                    }}
                  >
                    ❌ Abbrechen
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* ===== MESSAGE LIST ===== */}
          <div style={{ display: 'grid', gap: '8px' }}>
            {messages.map((msg) => (
              <div key={msg.id}>
                <div
                  onClick={() => toggleMessageSelection(msg)}
                  style={{
                    background:
                      selectedMessage?.id === msg.id
                        ? 'rgba(168, 85, 247, 0.15)'
                        : 'rgba(30, 41, 59, 0.5)',
                    border: '1px solid rgba(168, 85, 247, 0.2)',
                    borderRadius: '8px',
                    padding: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                >
                  <div style={{ color: '#a855f7', fontWeight: '600' }}>
                    {msg.recipient_name}
                  </div>
                  <div style={{ fontSize: '12px', color: '#94a3b8' }}>
                    {msg.message_text.substring(0, 50)}...
                  </div>
                </div>

                {selectedMessage?.id === msg.id && (
                  <div style={{ marginTop: '8px' }}>
                    {editingMessage === msg.id ? (
                      <form
                        onSubmit={handleSaveEdit}
                        style={{
                          background: 'rgba(30, 41, 59, 0.5)',
                          border: '1px solid rgba(168, 85, 247, 0.2)',
                          borderRadius: '8px',
                          padding: '15px',
                        }}
                      >
                        <label style={{ display: 'block', color: '#94a3b8', marginBottom: '4px', fontSize: '11px' }}>
                          Empfänger Name
                        </label>
                        <input
                          type="text"
                          value={editFormData.recipient_name}
                          onChange={(e) => setEditFormData({ ...editFormData, recipient_name: e.target.value })}
                          placeholder="Empfänger"
                          style={{
                            width: '100%',
                            padding: '8px',
                            background: '#0f172a',
                            border: '1px solid rgba(168, 85, 247, 0.3)',
                            borderRadius: '4px',
                            color: '#e2e8f0',
                            boxSizing: 'border-box',
                            marginBottom: '8px',
                          }}
                        />
                        <label style={{ display: 'block', color: '#94a3b8', marginBottom: '4px', fontSize: '11px' }}>
                          Nachricht
                        </label>
                        <textarea
                          value={editFormData.message_text}
                          onChange={(e) => setEditFormData({ ...editFormData, message_text: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '8px',
                            background: '#0f172a',
                            border: '1px solid rgba(168, 85, 247, 0.3)',
                            borderRadius: '4px',
                            color: '#e2e8f0',
                            minHeight: '80px',
                            boxSizing: 'border-box',
                            fontFamily: 'Georgia, serif',
                            marginBottom: '8px',
                          }}
                        />
                        <label style={{ display: 'block', color: '#94a3b8', marginBottom: '4px', fontSize: '11px' }}>
                          Freigabedatum
                        </label>
                        <input
                          type="datetime-local"
                          value={editFormData.release_date}
                          onChange={(e) => setEditFormData({ ...editFormData, release_date: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '8px',
                            background: '#0f172a',
                            border: '1px solid rgba(168, 85, 247, 0.3)',
                            borderRadius: '4px',
                            color: '#e2e8f0',
                            boxSizing: 'border-box',
                            marginBottom: '8px',
                          }}
                        />
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            type="submit"
                            style={{
                              flex: 1,
                              padding: '8px',
                              background: '#10b981',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px',
                            }}
                          >
                            ✅ Speichern
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditingMessage(null)}
                            style={{
                              flex: 1,
                              padding: '8px',
                              background: '#6b7280',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px',
                            }}
                          >
                            ❌ Abbrechen
                          </button>
                        </div>
                      </form>
                    ) : (
                      <>
                        <LetterPreview
                          name={msg.recipient_name}
                          text={msg.message_text}
                          image={msg.message_image}
                        />

                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '12px' }}>
                          <button
                            onClick={() => startEdit(msg)}
                            style={{
                              flex: '1 1 auto',
                              minWidth: '100px',
                              padding: '8px 16px',
                              background: '#3b82f6',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '12px',
                            }}
                          >
                            ✏️ Bearbeiten
                          </button>
                          <button
                            onClick={() => handleDeleteMessage(msg.id)}
                            style={{
                              flex: '1 1 auto',
                              minWidth: '100px',
                              padding: '8px 16px',
                              background: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '12px',
                            }}
                          >
                            🗑️ Löschen
                          </button>
                          <button
                            onClick={() => {
                              setShowQRLink(msg.id);
                              setCurrentQRId(msg.id);
                            }}
                            style={{
                              flex: '1 1 auto',
                              minWidth: '100px',
                              padding: '8px 16px',
                              background: '#f59e0b',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '12px',
                            }}
                          >
                            📱 QR/Link
                          </button>
                        </div>

                        {showQRLink === msg.id && (
                          <div
                            style={{
                              marginTop: '12px',
                              background: 'rgba(30, 41, 59, 0.5)',
                              border: '1px solid rgba(168, 85, 247, 0.3)',
                              borderRadius: '6px',
                              padding: '15px',
                            }}
                          >
                            <h4 style={{ color: '#a855f7', marginTop: 0, marginBottom: '15px' }}>
                              📱 Deine Postkarte
                            </h4>

                            {/* Postcard with QR - 3 Line Preview */}
                            <PostcardLayout
                              name={msg.recipient_name}
                              text={msg.message_text}
                              senderName={user.email?.split('@')[0] || 'admin'}
                              qrUrl={`${window.location.origin}/view/${msg.token || 'N/A'}`}
                              qrId={`msg_${msg.id}`}
                            />

                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '12px' }}>
                              <button
                                onClick={() => {
                                  setCurrentQRId(`msg_${msg.id}`);
                                  downloadQR();
                                }}
                                style={{
                                  flex: '1 1 auto',
                                  minWidth: '100px',
                                  padding: '8px 12px',
                                  background: '#a855f7',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                }}
                              >
                                ⬇️ Postkarte downloaden
                              </button>
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(`${window.location.origin}/view/${msg.token || 'N/A'}`);
                                  alert('✅ Link kopiert!');
                                }}
                                style={{
                                  flex: '1 1 auto',
                                  minWidth: '100px',
                                  padding: '8px 12px',
                                  background: '#10b981',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                }}
                              >
                                📋 Link kopieren
                              </button>
                              <button
                                onClick={() => setShowQRLink(null)}
                                style={{
                                  flex: '1 1 auto',
                                  minWidth: '100px',
                                  padding: '8px 12px',
                                  background: '#6b7280',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                }}
                              >
                                ❌ Schließen
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      ) : (
        <div style={{ padding: '20px' }}>
          <h3 style={{ color: '#a855f7', marginBottom: '20px' }}>
            📱 Deine Postkarte
          </h3>

          <PostcardLayout
            name={qrData?.recipient_name}
            text="Diese Nachricht wurde speziell für dich hinterlegt und wartet auf den richtigen Zeitpunkt..."
            senderName={user.email?.split('@')[0] || 'admin'}
            qrUrl={qrData.url}
            qrId="main"
          />

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '20px' }}>
            <button
              onClick={() => {
                setCurrentQRId('main');
                downloadQR();
              }}
              style={{
                padding: '10px 20px',
                background: '#a855f7',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '600',
              }}
            >
              ⬇️ Postkarte downloaden
            </button>
            <button
              onClick={() => {
                navigator.clipboard.writeText(qrData.url);
                alert('✅ Link kopiert!');
              }}
              style={{
                padding: '10px 20px',
                background: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '600',
              }}
            >
              📋 Link kopieren
            </button>
            <button
              onClick={() => {
                setQrData(null);
                setShowForm(false);
              }}
              style={{
                padding: '10px 20px',
                background: 'transparent',
                color: '#a855f7',
                border: '1px solid rgba(168, 85, 247, 0.5)',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '600',
              }}
            >
              ❌ Schließen
            </button>
          </div>
        </div>
      )}
      
      {/* ===== END OF MAIN CONTAINER ===== */}
    </div>
  );
}

export default Messages;
